/**
 * Validates that an RSS article has real, usable content.
 * Admin articles (isRss: false) are always considered valid.
 * Returns false if the article is a stub, broken, access-denied, has an
 * incomplete / placeholder summary, or lacks enough content for 3 paragraphs.
 */

const INVALID_PATTERNS = [
  /access\s+denied/i,
  /you\s+don['']t\s+have\s+permission/i,
  /reference\s+#\s*\d+/i,
  /errors\.edgesuite\.net/i,
  /403\s+forbidden/i,
  /cf-error-details/i,
  /ray\s+id\s*:/i,
  /error\s+1020/i,
];

// Summaries that indicate a failed or placeholder generation — never usable
const INCOMPLETE_SUMMARY_PATTERNS = [
  /^summary\s+unavailable\.?$/i,
  /^could\s+not\s+summarize\.?$/i,
  /^content\s+not\s+available\.?$/i,
  /^loading\.{0,3}$/i,
  /^no\s+summary(\s+available)?\.?$/i,
  /^summarization\s+failed\.?$/i,
  /^article\s+summary\s+not\s+available\.?$/i,
  /^सारांश\s+उपलब्ध\s+नहीं\.?$/i,
  /^सामग्री\s+उपलब्ध\s+नहीं\.?$/i,
];

// Minimum readable summary length (chars) when no body text is present
const MIN_SUMMARY_LENGTH = 100;

// Minimum quality sentences in combined (summary + body) content.
// buildLocalSummary distributes sentences into paragraph groups — with fewer than
// MIN_QUALITY_SENTENCES it cannot produce 3 meaningful paragraphs.
const MIN_QUALITY_SENTENCES = 3;

const stripHtml = (html) =>
  html.replace(/<[^>]*>/g, '').replace(/&[a-z]+;/gi, ' ').trim();

// Splits plain text into quality sentences (≥15 chars each), mirroring
// buildLocalSummary's splitSentences logic.
const extractQualitySentences = (text) => {
  if (!text || text.length < 20) return [];
  return text
    .replace(/([।.!?])\s+/g, '$1\n')
    .split('\n')
    .map(s => s.trim())
    .filter(s => s.length >= 15);
};

// Returns true when combined (summary + body) has enough quality sentences for
// buildLocalSummary to produce 3 meaningful paragraphs.
// body may still contain HTML — we strip tags before counting.
const hasSufficientContentForSummary = (summary, body) => {
  const plainSummary = stripHtml(summary || '');
  const plainBody    = (body || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  const combined     = (plainSummary + ' ' + plainBody).trim();
  return extractQualitySentences(combined).length >= MIN_QUALITY_SENTENCES;
};

// Returns true when the same sentence appears 3+ times — junk feed content
const hasMostlyRepeatedContent = (text) => {
  if (text.length < 80) return false;
  const sentences = text.split(/[।.!?\n]+/).map(s => s.trim()).filter(s => s.length > 15);
  if (sentences.length < 3) return false;
  const seen = new Map();
  for (const s of sentences) {
    const key = s.toLowerCase().replace(/\s+/g, ' ');
    seen.set(key, (seen.get(key) || 0) + 1);
    if (seen.get(key) >= 3) return true;
  }
  return false;
};

// Normalize to bare words for title-vs-summary comparison.
const normForCompare = (s) =>
  (s || '').toLowerCase().replace(/[^a-z0-9ऀ-ॿ\s]/g, '').replace(/\s+/g, ' ').trim();

// Returns true when the summary is essentially a copy/echo of the headline:
// exact match, summary that starts with the full title, or high word-overlap
// in a short summary (the AI just regurgitated the headline).
const isSummaryTitleDupe = (summary, title) => {
  if (!title || !summary) return false;
  const t = normForCompare(title);
  const s = normForCompare(summary);
  if (!t || !s) return false;
  if (s === t) return true;
  if (t.length > 20 && s.startsWith(t)) return true;
  if (s.length < t.length * 2) {
    const titleWords = t.split(' ').filter(w => w.length > 3);
    const summaryWordSet = new Set(s.split(' ').filter(w => w.length > 3));
    if (titleWords.length === 0) return false;
    const overlap = titleWords.filter(w => summaryWordSet.has(w)).length;
    if (overlap / titleWords.length > 0.72 && s.length < 280) return true;
  }
  return false;
};

export const isValidArticle = (article) => {
  if (!article) return false;

  // Admin-written articles are always valid — never filtered
  if (!article.isRss) return true;

  const title   = article.title   || '';
  const summary = article.summary || '';
  const body    = article.body    || '';

  // Fail if any error/access-denied pattern appears anywhere in the content
  const combined = `${title} ${summary} ${body}`;
  if (INVALID_PATTERNS.some(re => re.test(combined))) return false;

  const summaryText = stripHtml(summary);

  // No summary and no body — nothing meaningful to show
  if (!summaryText && !body) return false;

  // Placeholder or failed-generation text — hide even if body exists
  if (INCOMPLETE_SUMMARY_PATTERNS.some(re => re.test(summaryText))) return false;

  // Reject stubs: summary under minimum length with no body content
  if (summaryText.length < MIN_SUMMARY_LENGTH && !body) return false;

  // Reject summaries that end with a question mark — these are echoed headlines, not summaries
  if (/[?؟]\s*$/.test(summaryText.trimEnd())) return false;

  // Reject summaries ending with truncation markers (article was cut off mid-sentence)
  if (/(\.\.\.|…)\s*$/.test(summaryText.trimEnd()) && !body) return false;

  // Reject summaries ending with hanging punctuation (incomplete sentence artefact)
  if (/[,;:]\s*$/.test(summaryText.trimEnd())) return false;

  // Reject summaries that are essentially a copy of the article title
  if (isSummaryTitleDupe(summaryText, title)) return false;

  // Reject stubs that end mid-sentence regardless of length (truncation artefact)
  const endsWithEllipsis = summaryText.endsWith('...') || summaryText.endsWith('…');
  if (endsWithEllipsis && summaryText.length < 150 && !body) return false;

  // Reject feeds that repeat the same sentence as filler content
  if (hasMostlyRepeatedContent(summaryText)) return false;

  // Require enough quality sentences for the article detail to show 3 paragraphs.
  // Articles with only 1–2 sentences cannot fill 3 paragraph groups in buildLocalSummary,
  // and also lack sufficient source material for a complete AI summary.
  if (!hasSufficientContentForSummary(summary, body)) return false;

  return true;
};
